import { isTaakAfgerond } from './projectMeta';
import { useMemo } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { T, Icon, stitchPill, stitchProgressSx } from './stitch';
import { taskTypeColors } from './projectMeta';
import TaskItem from './TaskItem';


function GroupedTasksView({ tasks, onTaskClick, onToggleComplete }) {
  // Group tasks by deliverable name (extract from task name after " - ")
  const groupedTasks = useMemo(() => {
    const groups = {};

    tasks.forEach(task => {
      const taskName = task.fields?.NaamTaak || '';
      // Try to extract deliverable name: "Montage V1 - Project Name" → "Project Name"
      // Or group by task type if no " - " separator: "Animatie DEF - 9056-03. Name" → "9056-03. Name"
      const parts = taskName.split(' - ');
      let deliverable = 'Overige taken';
      let taskType = '';

      if (parts.length >= 2) {
        taskType = parts[0].trim(); // "Montage V1", "Animatie DEF", etc.
        deliverable = parts.slice(1).join(' - ').trim(); // Everything after first " - "
      } else {
        deliverable = taskName;
      }

      if (!groups[deliverable]) {
        groups[deliverable] = {
          name: deliverable,
          tasks: [],
          taskTypes: new Set(),
        };
      }
      groups[deliverable].tasks.push(task);
      if (taskType) groups[deliverable].taskTypes.add(taskType);
    });

    // Convert to array and sort by number of tasks (largest groups first)
    return Object.values(groups).sort((a, b) => b.tasks.length - a.tasks.length);
  }, [tasks]);

  // Count completed tasks
  const getCompletedCount = (taskList) => {
    return taskList.filter(isTaakAfgerond).length;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {groupedTasks.map((group, idx) => {
        const completedCount = getCompletedCount(group.tasks);
        const totalCount = group.tasks.length;
        const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
        const taskTypes = Array.from(group.taskTypes);

        return (
          <Accordion
            key={idx}
            defaultExpanded={idx < 3} // Expand first 3 groups
            sx={{
              boxShadow: T.shadow,
              borderRadius: '0.75rem !important',
              '&:before': { display: 'none' },
              '&.Mui-expanded': { margin: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<Icon name="expand_more" size={24} />}
              sx={{
                minHeight: 56,
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  gap: 2,
                },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontFamily: T.font, fontWeight: 600 }}>
                    {group.name}
                  </Typography>
                  <Typography component="span" sx={stitchPill(
                    progress === 100 ? '#166534' : T.outline,
                    progress === 100 ? '#dcfce7' : T.surfaceLow
                  )}>
                    {`${completedCount}/${totalCount}`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {taskTypes.slice(0, 3).map((type, i) => (
                    <Typography
                      key={i}
                      component="span"
                      sx={{
                        ...stitchPill(taskTypeColors[type.split(' ')[0]] || T.outline, 'transparent'),
                        border: `1px solid ${taskTypeColors[type.split(' ')[0]] || T.outlineVariant}`,
                        fontSize: '0.55rem',
                      }}
                    >
                      {type}
                    </Typography>
                  ))}
                  {taskTypes.length > 3 && (
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
                      +{taskTypes.length - 3}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Progress bar */}
              <Box sx={{ width: 100 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    ...stitchProgressSx,
                    '& .MuiLinearProgress-bar': {
                      background: progress === 100 ? T.emerald : `linear-gradient(135deg, ${T.tertiary}, ${T.primary})`,
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0, pb: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {group.tasks.map(task => (
                  <TaskItem key={task.id} task={task} compact onClick={() => onTaskClick?.(task)} onToggleComplete={onToggleComplete} />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

export default GroupedTasksView;
